// BUILD-046 J1a (Customer / Job association): the "Customer / Job" filter of the toolbar's Filter popover (My Tasks, Shared Tasks, Team / Company).
//
// It is a CLIENT-SIDE display filter of the rows the page has loaded, exactly like the Team department filter: it keeps the rows of one job, or the rows with no
// job, or (the default) every row. Its choices are the jobs the loaded rows carry, named as the rows show them (the server's current name, or the last known one),
// so the filter can never offer a job no loaded task has. It is applied inside viewListModel.deriveViewList, before the search and the sort, and it narrows the
// ROWS of a view, never the labelled counts of the page (those come from the whole list). The default is "all" and nothing is stored: saved views are a later
// increment (J4).
//
// A job the person chose that no loaded row carries any more (the list was reloaded, a task was completed) stays chosen and stays in the choices, so the
// control never shows a value it does not list, and an empty result reads as "no tasks match", not as a bug.
//
// Pure module: no React, no I/O. Erasable TypeScript; the only import is the pure display model.

import { jobLineOf, jobOf } from './jobDisplayModel';
import { stripTestMarker } from './displayNames';

export type JobFilter = { kind: 'any' } | { kind: 'none' } | { kind: 'job'; jobId: string; label: string };

export const ANY_JOB: JobFilter = Object.freeze({ kind: 'any' } as const);

/** The choice values of the control: `any`, `none`, or a job id. A job id can never equal the other two (they are not valid ids). */
export const JOB_FILTER_ANY_VALUE = 'any';
export const JOB_FILTER_NONE_VALUE = 'none';

export interface JobFilterOption {
  /** `any`, `none` or the job id. */
  value: string;
  label: string;
  /** How many loaded rows carry this choice (for `any`, all of them); informational. */
  count: number;
}

export function jobFilterActive(filter: JobFilter): boolean {
  return filter.kind !== 'any';
}

export function jobFilterValue(filter: JobFilter): string {
  return filter.kind === 'any' ? JOB_FILTER_ANY_VALUE : filter.kind === 'none' ? JOB_FILTER_NONE_VALUE : filter.jobId;
}

/** Keeps the rows the filter asks for. `any` keeps every row. A row shown in its minimal form carries no job, so it counts as "no job". */
export function filterByJob<T>(rows: readonly T[], filter: JobFilter): readonly T[] {
  if (filter.kind === 'any') return rows;
  if (filter.kind === 'none') return rows.filter((row) => jobOf(row) === null);
  return rows.filter((row) => jobOf(row)?.jobId === filter.jobId);
}

/** The distinct jobs of the rows, named as the rows name them, in name order (numbers in order: "Lot 2" before "Lot 12"), with how many rows carry each. */
function distinctJobs(rows: readonly unknown[]): { jobId: string; label: string; count: number }[] {
  const byId = new Map<string, { jobId: string; label: string; count: number }>();
  for (const row of rows) {
    const job = jobOf(row);
    if (job === null) continue;
    const seen = byId.get(job.jobId);
    if (seen === undefined) byId.set(job.jobId, { jobId: job.jobId, label: job.label, count: 1 });
    else seen.count += 1;
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.jobId.localeCompare(b.jobId));
}

/** Whether any of the rows carries a job. The Job control is offered only then (or while a job filter is active). */
export function hasAnyJob(rows: readonly unknown[]): boolean {
  return rows.some((row) => jobOf(row) !== null);
}

/**
 * The choices: All jobs, No job, then each job the loaded rows carry. The chosen job is always among them, even when no loaded row carries it (its count is 0),
 * so the control never shows a value it does not list.
 */
export function jobFilterOptions(rows: readonly unknown[], filter: JobFilter): JobFilterOption[] {
  const jobs = distinctJobs(rows);
  const withJob = jobs.reduce((sum, j) => sum + j.count, 0);
  const options: JobFilterOption[] = [
    { value: JOB_FILTER_ANY_VALUE, label: 'Any job', count: rows.length },
    { value: JOB_FILTER_NONE_VALUE, label: 'Without a job', count: rows.length - withJob },
    // Sorted above by the stored name; SHOWN without the "(FICTIONAL)" test marker.
    ...jobs.map((j) => ({ value: j.jobId, label: stripTestMarker(j.label), count: j.count })),
  ];
  if (filter.kind === 'job' && !jobs.some((j) => j.jobId === filter.jobId)) options.push({ value: filter.jobId, label: stripTestMarker(filter.label), count: 0 });
  return options;
}

/** The filter a choice stands for. A value that is not one of the choices leaves the filter as it was (nothing is guessed). */
export function chooseJobFilter(value: unknown, options: readonly JobFilterOption[], current: JobFilter): JobFilter {
  if (value === JOB_FILTER_ANY_VALUE) return ANY_JOB;
  if (value === JOB_FILTER_NONE_VALUE) return { kind: 'none' };
  if (typeof value !== 'string') return current;
  const option = options.find((o) => o.value === value);
  return option === undefined ? current : { kind: 'job', jobId: option.value, label: option.label };
}

/** What the control says under the choices: the choices are the jobs of the tasks loaded so far, which is every task only when the list is whole. */
export function jobFilterNote(complete: boolean): string | null {
  return complete ? null : 'Lists the jobs on the tasks loaded so far.';
}

/** The three views that can have a job filter, and the filter each holds. */
export type JobFilterView = 'my-work' | 'group-shared' | 'team-work';
export type JobFilters = Readonly<Record<JobFilterView, JobFilter>>;

export function initialJobFilters(): JobFilters {
  return { 'my-work': ANY_JOB, 'group-shared': ANY_JOB, 'team-work': ANY_JOB };
}

export function setJobFilter(filters: JobFilters, view: JobFilterView, next: JobFilter): JobFilters {
  const current = filters[view];
  if (current === undefined) return filters;
  if (current.kind === next.kind && jobFilterValue(current) === jobFilterValue(next)) return filters;
  return { ...filters, [view]: next };
}

// ═════════════════════════════ the searchable Customer / Job picker (BUILD-046 cycle C, Edit 3) ═════════════════════════════
//
// The Filter popover no longer shows a native drop-down of every job. It shows a search box, the two quick choices (All jobs, No job) and a short hint, and lists
// matches only after the person types (at least one character), at most eight at a time. Everything below is the pure part of that control; the components
// (JobFilterPicker.tsx, JobFilterChip.tsx) only draw it.
//
//   - OPTIONS are the distinct jobs on the rows the view has LOADED and the person may see (a redacted row carries no job). The page passes the view's whole
//     loaded set BEFORE the status tab narrows it, so a job that is only on a Waiting or Completed task is still found. A HISTORICAL job (archived, not found,
//     shown by its last known name) is an option like any other: nothing is hidden or demoted because of the job's state, the task's status or its age.
//   - SEARCH matches the job NAME, and the customer and the code WHERE THE ROW SUPPLIES THEM. A task view carries `{ jobId, label, state, lastKnown, missing }`
//     only (jobDisplayModel.JobShown): no code and no customer, so today the search covers names ("codes not supplied on task views"). Matching ignores case and
//     accents, and every word typed must be found (in any order).
//   - The VALUE of the filter stays the stable job id (`any`, `none` or a job id, as before). The name is only for reading; it is never the key.
//   - The SCOPE sentence says which tasks the options come from, and for a list read page by page that loading more can add other jobs: the control never claims
//     to search every task.

/** The visible name of the control and of its chip. */
export const JOB_FILTER_LABEL = 'Customer / Job';
/** BUILD-046 (Customer association): the name of the same control while the Customer filter is also offered, so the two never share a name. Everything else about the job filter is unchanged. */
export const JOB_FILTER_LABEL_BESIDE_CUSTOMER = 'Job';
/** The most matches shown at once. */
export const JOB_FILTER_RESULT_LIMIT = 8;

/** A job offered by the picker: what the loaded rows show of it, plus a customer and a code when a row supplies them (task views do not today). */
export interface JobChoiceOption {
  jobId: string;
  label: string;
  state?: 'active' | 'archived';
  lastKnown?: true;
  missing?: true;
  customer?: string;
  code?: string;
}

function textOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function optionOf(row: unknown): JobChoiceOption | null {
  const job = jobOf(row);
  if (job === null) return null;
  const option: JobChoiceOption = { jobId: job.jobId, label: job.label };
  if (job.state !== undefined) option.state = job.state;
  if (job.lastKnown === true) option.lastKnown = true;
  if (job.missing === true) option.missing = true;
  const carrier = (row as { job?: { customer?: unknown; code?: unknown } }).job;
  const customer = textOrEmpty(carrier?.customer);
  const code = textOrEmpty(carrier?.code);
  if (customer !== '') option.customer = customer;
  if (code !== '') option.code = code;
  return option;
}

/**
 * The jobs the picker offers: the distinct jobs of ALL the given rows, in name order (numbers in order: "Lot 2" before "Lot 12"). `rowSets` are every list the view
 * has loaded (Team / Company: the open list and, when it was read, the completed list). Rows of any status and any age count; a job that appears on several rows is
 * one option, named as a row that was checked against the job list names it (a stored last-known name only when no row has the current one). The chosen job is
 * always among the options, even when no loaded row carries it any more, so the control never shows a value it does not list.
 */
export function jobChoicesOf(rowSets: readonly (readonly unknown[])[], filter: JobFilter): JobChoiceOption[] {
  const byId = new Map<string, JobChoiceOption>();
  for (const rows of rowSets) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const option = optionOf(row);
      if (option === null) continue;
      const seen = byId.get(option.jobId);
      if (seen === undefined || (seen.lastKnown === true && option.lastKnown !== true)) byId.set(option.jobId, option);
    }
  }
  const options = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.jobId.localeCompare(b.jobId));
  if (filter.kind === 'job' && !byId.has(filter.jobId)) options.push({ jobId: filter.jobId, label: filter.label });
  return options;
}

/** The quiet word after a job's name when the name may be out of date or the job is retired (`archived`, `not found`, `last known name`); null when it is plain. */
export function jobChoiceNote(option: JobChoiceOption): string | null {
  return jobLineOf({ jobId: option.jobId, label: option.label, state: option.state, lastKnown: option.lastKnown, missing: option.missing }).note;
}

/** Lower case, accents removed, white space collapsed: what a name and what is typed are compared as. */
export function normalizeJobSearchText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type JobSearchStatus = 'idle' | 'results' | 'none';

export interface JobSearchOutcome {
  /** `idle`: nothing typed, so nothing is listed. `results`: at least one match. `none`: something was typed and no job matches. */
  status: JobSearchStatus;
  /** What was typed, trimmed (for "No jobs match ..."). */
  query: string;
  /** At most `limit` matches: names that begin with what was typed first, then names with a word that does, then the rest, each group in name order. */
  matches: JobChoiceOption[];
  /** How many jobs match in all. */
  total: number;
  /** More jobs match than are shown. */
  more: boolean;
}

function haystackOf(option: JobChoiceOption): string {
  return normalizeJobSearchText([option.label, option.customer ?? '', option.code ?? ''].join(' '));
}

/**
 * The jobs that match what was typed, at most `limit` (default 8). Nothing is listed until at least one character is typed. Every word typed must be found in the
 * job name (or in the customer or code, where a row supplies them), ignoring case and accents, in any order.
 */
export function searchJobOptions(options: readonly JobChoiceOption[], query: unknown, limit: number = JOB_FILTER_RESULT_LIMIT): JobSearchOutcome {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : JOB_FILTER_RESULT_LIMIT;
  const typed = typeof query === 'string' ? query.replace(/\s+/g, ' ').trim() : '';
  const q = normalizeJobSearchText(typed);
  if (q === '') return { status: 'idle', query: typed, matches: [], total: 0, more: false };
  const words = q.split(' ');
  const ranked: { option: JobChoiceOption; rank: number; at: number }[] = [];
  options.forEach((option, at) => {
    const hay = haystackOf(option);
    if (!words.every((word) => hay.includes(word))) return;
    const name = normalizeJobSearchText(option.label);
    const rank = name.startsWith(q) ? 0 : name.split(' ').some((part) => part.startsWith(words[0])) ? 1 : 2;
    ranked.push({ option, rank, at });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.at - b.at);
  const total = ranked.length;
  const matches = ranked.slice(0, cap).map((r) => r.option);
  return { status: total === 0 ? 'none' : 'results', query: typed, matches, total, more: total > cap };
}

export interface JobPickerView {
  status: JobSearchStatus;
  /** The line under the matches when more jobs match than are shown ("Showing 8 of 21. Keep typing to narrow."); null otherwise. */
  moreNote: string | null;
  /** "No jobs match ..." only after something was typed and nothing matches; null otherwise. */
  emptyText: string | null;
  /** The short hint while nothing is typed; null otherwise. */
  hint: string | null;
  /** The polite line for assistive technology; '' while idle. */
  announcement: string;
}

export const JOB_PICKER_HINT = 'Type a customer or job name to see matches.';

export function describeJobPicker(outcome: JobSearchOutcome, limit: number = JOB_FILTER_RESULT_LIMIT): JobPickerView {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : JOB_FILTER_RESULT_LIMIT;
  if (outcome.status === 'idle') return { status: 'idle', moreNote: null, emptyText: null, hint: JOB_PICKER_HINT, announcement: '' };
  if (outcome.status === 'none') return { status: 'none', moreNote: null, emptyText: `No jobs match "${outcome.query}".`, hint: null, announcement: 'No jobs match.' };
  const shown = outcome.matches.length;
  const moreNote = outcome.more ? `Showing ${cap} of ${outcome.total}. Keep typing to narrow.` : null;
  const found = `${shown} ${shown === 1 ? 'job' : 'jobs'} found.`;
  return { status: 'results', moreNote, emptyText: null, hint: null, announcement: `${moreNote !== null ? `${moreNote} ` : `${found} `}Use the arrow keys, then Enter to choose.` };
}

/** The two quick choices, in the order shown. Their values are the filter values `any` and `none`. */
export const JOB_QUICK_CHOICES: readonly { value: 'any' | 'none'; label: string }[] = Object.freeze([
  { value: 'any' as const, label: 'Any job' },
  { value: 'none' as const, label: 'Without a job' },
]);

/** The filter a quick choice stands for. */
export function jobFilterFromQuick(value: 'any' | 'none'): JobFilter {
  return value === 'none' ? { kind: 'none' } : ANY_JOB;
}

/** The filter a chosen job stands for: the STABLE ID is the value and the name is only for reading. A choice without a usable id or name gives null (nothing is guessed). */
export function jobFilterFromOption(option: unknown): JobFilter | null {
  if (option === null || typeof option !== 'object') return null;
  const o = option as { jobId?: unknown; label?: unknown };
  if (typeof o.jobId !== 'string' || o.jobId === '' || o.jobId === JOB_FILTER_ANY_VALUE || o.jobId === JOB_FILTER_NONE_VALUE) return null;
  if (typeof o.label !== 'string' || o.label.trim() === '') return null;
  return { kind: 'job', jobId: o.jobId, label: o.label.trim() };
}

/** What the filter is, in words, for the panel: "All jobs", "No job" or the job's name. */
export function jobFilterSummary(filter: JobFilter): string {
  // The job's name is SHOWN without the "(FICTIONAL)" test marker; the filter keeps the stored label and its value is the job id.
  return filter.kind === 'any' ? 'Any job' : filter.kind === 'none' ? 'Without a job' : stripTestMarker(filter.label);
}

export interface JobFilterChipView {
  /** "Customer / Job: Smith Residence" or "Customer / Job: No job". */
  text: string;
  /** The Remove button's accessible name. */
  removeLabel: string;
}

/** The removable chip above the table: none for All jobs, so the chip always means a filter is on. */
export function describeJobFilterChip(filter: JobFilter, label: string = JOB_FILTER_LABEL): JobFilterChipView | null {
  if (filter.kind === 'any') return null;
  return { text: `${label}: ${jobFilterSummary(filter)}`, removeLabel: `Remove ${label} filter` };
}

/** Removing the chip clears ONLY this view's job filter. Every other view's filter is the same object; nothing else lives in this state. Already off: the same object. */
export function removeJobFilter(filters: JobFilters, view: JobFilterView): JobFilters {
  return setJobFilter(filters, view, ANY_JOB);
}

/** Which tasks the picker's options come from. `whole`: the list is whole. `paged`: more pages exist. `open-only`: Team / Company with only the open list read. */
export type JobListKind = 'whole' | 'paged' | 'open-only';

/**
 * The sentence under the picker. It names where the jobs come from, never claims to search every task, and says that loading more can add other jobs where a
 * list is read page by page (Team / Company Completed, Assigned by Me).
 */
export function jobFilterScopeText(kind: JobListKind, loadedCount: number, subject: string = 'tasks'): string {
  const n = Number.isInteger(loadedCount) && loadedCount > 0 ? loadedCount : 0;
  const what = `${n} ${n === 1 ? subject.replace(/s$/, '') : subject}`;
  if (kind === 'paged') return `Jobs shown are those on the ${what} loaded so far. Loading more can add other jobs; this does not search every task.`;
  if (kind === 'open-only') return `Jobs shown are those on the ${what} loaded here. Completed tasks are included once Completed is opened.`;
  return `Jobs shown are those on the ${what} loaded in this list.`;
}

export interface JobFilterEmptyView {
  headline: string;
  detail: string;
  clearLabel: string;
}

/**
 * The empty state when a job is selected and no row is left. It names the job filter (and the status choice and the other filters when they also narrow) and offers
 * to clear it; it never says "no tasks" as if nothing existed. `statusText` is the status choice that also narrows ("Status: Waiting"), or null; `otherNarrowing` is true
 * when a search, a department or a quick filter also applies; `complete` is false for a list read page by page.
 */
export function describeJobFilterEmpty(filter: JobFilter, statusText: string | null, otherNarrowing: boolean, complete: boolean, label: string = JOB_FILTER_LABEL): JobFilterEmptyView | null {
  const chip = describeJobFilterChip(filter, label);
  if (chip === null) return null;
  const withStatus = statusText !== null && statusText.trim() !== '' ? ` with ${statusText.trim()}` : '';
  const withOthers = otherNarrowing ? ' and your other filters or search' : '';
  return {
    headline: `No tasks match ${chip.text}${withStatus}${withOthers}.`,
    detail: complete ? `Clear the ${label} filter to see the tasks again.` : `Only the tasks loaded so far were checked. Load more, or clear the ${label} filter.`,
    clearLabel: `Clear ${label} filter`,
  };
}
